import React, { useEffect, useRef, useState } from "react";
import {
  getAthleteProfile,
  updateAthleteProfile,
  insertAthleteFileService,
  deleteAthleteFileService,
} from "../../../services/AthleteService";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowUpFromBracket,
  faEnvelope,
  faPenToSquare,
  faPhone,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { useAxiosInstance } from "../../../services/Interceptor";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";
import { useDispatch, useSelector } from "react-redux";
import { positions, states } from "../../../utils/PositionStateData";
import { faInstagram, faXTwitter } from "@fortawesome/free-brands-svg-icons";
import ConfirmModal from "../../../components/ConfirmModel";
import PremiumFeatureModal from "../../../components/PremiumFeatureModal";
import FreeTrialModal from "../../../components/FreeTrialModal";
import { StartFreeTrial } from "../../../services/PaymentService";
import { usePremiumUpsellRedirect } from "../../../hooks/usePremiumUpsellRedirect";
import { setProfileAction } from "../../../store/AthleteActions";
import { validateSubscriptionAction } from "../../../store/userActions";
import { bumpMenuRefresh } from "../../../store/slices/athleteSlice";

export const PersonalInfoAthlete = () => {
  const authUser = useAuthUser();
  const { user = {} } = authUser || {};
  const dispatch = useDispatch();
  const hasSubscription = useSelector((state) => state.user.hasSubscription);
  const hasUsedFreeTrial = useSelector((state) => state.user.hasUsedFreeTrial);
  const hasSubscriptionAccess =
    hasSubscription || user?.role?.toLowerCase?.() === "highschool";
  const [profileData, setProfileData] = useState({
    firstName: "",
    lastName: "",
    gpa: "",
    highSchool: "",
    city: "",
    state: "",
    primaryPosition: "",
    secondaryPosition: "",
    universityOfInterest: "",
    academicDegreeOfInterest: "",
    instagramHandle: "",
    twitterHandle: "",
    athleteEmail: "",
    parentEmail: "",
    athletePhone: "",
    parentPhone: "",
    transcriptFilePath: "",
  });

  const [isEditing, setIsEditing] = useState(false);
  const apiInstance = useAxiosInstance();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [fileIdToDelete, setFileIdToDelete] = useState(null);
  const [openPremiumFeatureModal, setOpenPremiumFeatureModal] = useState(false);
  const [openTrialModal, setOpenTrialModal] = useState(false);
  const showFreeTrial =
    !hasSubscriptionAccess && user?.role === "athlete" && !hasUsedFreeTrial;

  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        const data = await getAthleteProfile(apiInstance, authUser?.user?.id);
        setProfileData((prev) => ({
          ...prev,
          ...data,
          firstName: data.firstName ?? data.name ?? "",
          lastName: data.lastName ?? data.lastname ?? "",
        }));
      } catch (error) {
        toast.error("Error fetching profile data");
      }
    };

    fetchProfileData();
  }, [authUser, apiInstance]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfileData({ ...profileData, [name]: value });
  };

  const handleEditToggle = () => {
    if (!hasSubscriptionAccess) {
      setOpenPremiumFeatureModal(true);
      return;
    }

    setIsEditing((prev) => !prev);
  };

  const openSubscription = usePremiumUpsellRedirect(user);

  const handlePremiumFreeTrial = () => {
    setOpenPremiumFeatureModal(false);
    setOpenTrialModal(true);
  };

  const handlePremiumSubscribe = () => {
    setOpenPremiumFeatureModal(false);
    openSubscription();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const updatedProfile = await updateAthleteProfile(
        apiInstance,
        authUser?.user?.id,
        profileData,
      );
      setProfileData(updatedProfile);
      toast.success("Profile updated successfully!");
      setIsEditing(false);
    } catch (error) {
      toast.error(
        "Error updating profile: " +
          (error.response?.data?.message || "Unknown error"),
      );
    }
  };

  const confirmDelete = async () => {
    const model = "AthleteFile";
    try {
      await deleteAthleteFileService(apiInstance, model, fileIdToDelete);
      toast.success("File deleted successfully!");
      setProfileData((prevState) => ({
        ...prevState,
        transcriptFilePath: "",
      }));
    } catch (error) {
      toast.error("Error to delete: " + error.response?.data?.message);
    } finally {
      setShowConfirmModal(false);
      setFileIdToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowConfirmModal(false);
    setFileIdToDelete(null);
  };

  return (
    <div className="bg-white shadow-lg p-4 rounded-lg border-gray-300">
      <ConfirmModal
        show={showConfirmModal}
        tital="Delete Transcript?"
        message="Are you sure you want to delete this file?"
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />
      <PremiumFeatureModal
        open={openPremiumFeatureModal}
        onClose={() => setOpenPremiumFeatureModal(false)}
        title="Premium Feature"
        description="Personal info updates are locked. Start a free trial or subscribe now to update your profile."
        onStartFreeTrial={handlePremiumFreeTrial}
        onSubscribe={handlePremiumSubscribe}
        showStartFreeTrial={showFreeTrial}
      />
      <FreeTrialModal
        open={openTrialModal}
        onClose={() => setOpenTrialModal(false)}
        user={user}
        onStoreSuccessChain={async () => {
          const result = await StartFreeTrial(apiInstance, user.id);
          if (result.success) {
            toast.success("Free trial started!", { position: "top-right" });
            setOpenTrialModal(false);
            await dispatch(setProfileAction(apiInstance, user.id));
            await dispatch(
              validateSubscriptionAction(apiInstance, user.role, user.id),
            );
            dispatch(bumpMenuRefresh());
          } else {
            toast.error("Failed to start free trial", {
              position: "top-right",
            });
          }
        }}
      />
      <div className="w-full">
        <div
          className="float-right text-gray-600 border border-gray-300 px-3 py-1 rounded bg-gray-300 cursor-pointer"
          onClick={handleEditToggle}
        >
          {isEditing ? "Cancel" : "Edit"}
          <FontAwesomeIcon
            icon={faPenToSquare}
            className="h-4 text-gray-600 ml-2"
          />
        </div>
        <h2 className="text-xl mb-4 uppercase">Personal Info</h2>

        <form onSubmit={handleSubmit} className="w-full">
          <div className="flex flex-wrap -mx-3">
            <div className="w-full md:w-1/2 px-3 pt-2">
              <label
                className="block uppercase tracking-wide text-gray-500 text-xs font-bold mb-2"
                htmlFor="firstName"
              >
                First Name
              </label>
              <input
                name="firstName"
                className={`appearance-none block w-full text-gray-900 border border-gray-300 rounded p-3 mb-3 leading-tight focus:outline-none ${!isEditing ? "focus:bg-gray-100" : "focus:bg-white"} placeholder-gray-900 ${!isEditing ? "bg-gray-100" : ""}`}
                id="firstName"
                type="text"
                value={profileData.firstName ?? ""}
                onChange={handleChange}
                readOnly={!isEditing}
              />
            </div>
            <div className="w-full md:w-1/2 px-3 pt-2">
              <label
                className="block uppercase tracking-wide text-gray-500 text-xs font-bold mb-2"
                htmlFor="lastName"
              >
                Last Name
              </label>
              <input
                name="lastName"
                className={`appearance-none block w-full text-gray-900 border border-gray-300 rounded p-3 mb-3 leading-tight focus:outline-none ${!isEditing ? "focus:bg-gray-100" : "focus:bg-white"} placeholder-gray-900 ${!isEditing ? "bg-gray-100" : ""}`}
                id="lastName"
                type="text"
                value={profileData.lastName ?? ""}
                onChange={handleChange}
                readOnly={!isEditing}
              />
            </div>
            <div className="w-full md:w-1/2 px-3 md:mb-0">
              <label
                className="block uppercase tracking-wide text-gray-500 text-xs font-bold mb-2"
                htmlFor="primaryPosition"
              >
                Position
              </label>
              <div className="relative">
                <select
                  name="primaryPosition"
                  className={`form-select bg-no-repeat block appearance-none w-full border border-gray-300 text-gray-900 p-3 pr-8 rounded leading-tight focus:outline-none ${!isEditing ? "focus:bg-gray-100" : "focus:bg-white"} focus:border-gray-500 ${!isEditing ? "bg-gray-100" : ""}`}
                  id="primaryPosition"
                  value={profileData.primaryPosition || ""}
                  onChange={handleChange}
                  disabled={!isEditing}
                >
                  {positions.map((position) => (
                    <option key={position} value={position}>
                      {position}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="w-full md:w-1/2 px-3 mb-6 md:mb-0">
              <label
                className="block uppercase tracking-wide text-gray-500 text-xs font-bold mb-2"
                htmlFor="secondaryPosition"
              >
                Secondary Position
              </label>
              <div className="relative">
                <select
                  name="secondaryPosition"
                  className={`form-select bg-no-repeat block appearance-none w-full border border-gray-300 text-gray-900 p-3 pr-8 rounded leading-tight focus:outline-none ${!isEditing ? "focus:bg-gray-100" : "focus:bg-white"} focus:border-gray-500 ${!isEditing ? "bg-gray-100" : ""}`}
                  id="secondaryPosition"
                  value={profileData.secondaryPosition || ""}
                  onChange={handleChange}
                  disabled={!isEditing}
                >
                  {positions.map((position) => (
                    <option key={position} value={position}>
                      {position}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="w-full md:w-1/2 px-3 pt-2">
              <label
                className="block uppercase tracking-wide text-gray-500 text-xs font-bold mb-2"
                htmlFor="highSchool"
              >
                High School
              </label>
              <input
                name="highSchool"
                className={`appearance-none block w-full text-gray-900 border border-gray-300 rounded p-3 mb-3 leading-tight focus:outline-none ${!isEditing ? "focus:bg-gray-100" : "focus:bg-white"} placeholder-gray-900 ${!isEditing ? "bg-gray-100" : ""}`}
                id="highSchool"
                type="text"
                value={profileData.highSchool ?? ""}
                onChange={handleChange}
                readOnly={!isEditing}
              />
            </div>
            <div className="w-full md:w-1/2 px-3 pt-2">
              <label
                className="block uppercase tracking-wide text-gray-500 text-xs font-bold mb-2"
                htmlFor="city"
              >
                City
              </label>
              <input
                name="city"
                className={`appearance-none block w-full text-gray-900 border border-gray-300 rounded p-3 mb-3 leading-tight focus:outline-none ${!isEditing ? "focus:bg-gray-100" : "focus:bg-white"} placeholder-gray-900 ${!isEditing ? "bg-gray-100" : ""}`}
                id="city"
                type="text"
                value={profileData.city ?? ""}
                onChange={handleChange}
                readOnly={!isEditing}
              />
            </div>{" "}
            <div className="w-full px-3 pt-1 mb-3">
              <label
                className="block uppercase tracking-wide text-gray-500 text-xs font-bold mb-2"
                htmlFor="state"
              >
                State
              </label>
              <div className="relative">
                <select
                  name="state"
                  className={`form-select bg-no-repeat block appearance-none w-full border border-gray-300 text-gray-900 p-3 pr-8 rounded leading-tight focus:outline-none ${!isEditing ? "focus:bg-gray-100" : "focus:bg-white"} focus:border-gray-500 ${!isEditing ? "bg-gray-100" : ""}`}
                  id="state"
                  value={profileData.state ?? ""}
                  onChange={handleChange}
                  disabled={!isEditing}
                >
                  {states.map((state) => (
                    <option key={state} value={state}>
                      {state}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap -mx-3">
            <div className="w-full md:w-1/2 px-3 md:mb-0">
              <label
                className="block uppercase tracking-wide text-gray-500 text-xs font-bold mb-2"
                htmlFor="universityOfInterest"
              >
                University of Interest
              </label>
              <input
                name="universityOfInterest"
                className={`appearance-none block w-full text-gray-900 border border-gray-300 rounded p-3 mb-3 leading-tight focus:outline-none ${!isEditing ? "focus:bg-gray-100" : "focus:bg-white"} placeholder-gray-900 ${!isEditing ? "bg-gray-100" : ""}`}
                id="universityOfInterest"
                type="text"
                value={profileData.universityOfInterest ?? ""}
                onChange={handleChange}
                readOnly={!isEditing}
              />
            </div>
            <div className="w-full md:w-1/2 px-3">
              <label
                className="block uppercase tracking-wide text-gray-500 text-xs font-bold mb-2"
                htmlFor="academicDegreeOfInterest"
              >
                Academic Degree of Interest
              </label>
              <input
                name="academicDegreeOfInterest"
                className={`appearance-none block w-full text-gray-900 border border-gray-300 rounded p-3 mb-3 leading-tight focus:outline-none ${!isEditing ? "focus:bg-gray-100" : "focus:bg-white"} placeholder-gray-900 ${!isEditing ? "bg-gray-100" : ""}`}
                id="academicDegreeOfInterest"
                type="text"
                value={profileData.academicDegreeOfInterest ?? ""}
                onChange={handleChange}
                readOnly={!isEditing}
              />
            </div>
          </div>

          <div className="flex flex-wrap -mx-3 mt-4">
            <div className="w-full md:w-1/2 px-3">
              <label
                className="block uppercase tracking-wide text-gray-500 text-xs font-bold mb-2"
                htmlFor="instagram"
              >
                Instagram
              </label>
              <div className="relative">
                <FontAwesomeIcon
                  icon={faInstagram}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                />
                <input
                  name="instagram"
                  className={`appearance-none block w-full text-gray-900 border border-gray-300 rounded p-3 pl-10 mb-3 leading-tight focus:outline-none ${!isEditing ? "focus:bg-gray-100" : "focus:bg-white"} placeholder-gray-900 ${!isEditing ? "bg-gray-100" : ""}`}
                  id="instagram"
                  type="text"
                  value={profileData.instagram ?? ""}
                  onChange={handleChange}
                  readOnly={!isEditing}
                />
              </div>
            </div>

            <div className="w-full md:w-1/2 px-3">
              <label
                className="block uppercase tracking-wide text-gray-500 text-xs font-bold mb-2"
                htmlFor="twitter"
              >
                Twitter
              </label>
              <div className="relative">
                <FontAwesomeIcon
                  icon={faXTwitter}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                />
                <input
                  name="twitter"
                  className={`appearance-none block w-full text-gray-900 border border-gray-300 rounded p-3 pl-10 mb-3 leading-tight focus:outline-none ${!isEditing ? "focus:bg-gray-100" : "focus:bg-white"} placeholder-gray-900 ${!isEditing ? "bg-gray-100" : ""}`}
                  id="twitter"
                  type="text"
                  value={profileData.twitter ?? ""}
                  onChange={handleChange}
                  readOnly={!isEditing}
                />
              </div>
            </div>
          </div>

          <div className="flex flex-wrap -mx-3 mt-4">
            <div className="w-full md:w-1/2 px-3">
              <label
                className="block uppercase tracking-wide text-gray-500 text-xs font-bold mb-2"
                htmlFor="athleteEmail"
              >
                Athlete Email
              </label>
              <div className="relative">
                <FontAwesomeIcon
                  icon={faEnvelope}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                />
                <input
                  name="athleteEmail"
                  className={`appearance-none block w-full text-gray-900 border border-gray-300 rounded p-3 pl-10 mb-3 leading-tight focus:outline-none ${!isEditing ? "focus:bg-gray-100" : "focus:bg-white"} placeholder-gray-900 ${!isEditing ? "bg-gray-100" : ""}`}
                  id="athleteEmail"
                  type="email"
                  value={profileData.athleteEmail ?? ""}
                  onChange={handleChange}
                  readOnly={!isEditing}
                />
              </div>
            </div>
            <div className="w-full md:w-1/2 px-3">
              <label
                className="block uppercase tracking-wide text-gray-500 text-xs font-bold mb-2"
                htmlFor="parentEmail"
              >
                Parent Email
              </label>
              <div className="relative">
                <FontAwesomeIcon
                  icon={faEnvelope}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                />
                <input
                  name="parentEmail"
                  className={`appearance-none block w-full text-gray-900 border border-gray-300 rounded p-3 pl-10 mb-3 leading-tight focus:outline-none ${!isEditing ? "focus:bg-gray-100" : "focus:bg-white"} placeholder-gray-900 ${!isEditing ? "bg-gray-100" : ""}`}
                  id="parentEmail"
                  type="email"
                  value={profileData.parentEmail ?? ""}
                  onChange={handleChange}
                  readOnly={!isEditing}
                />
              </div>
            </div>
          </div>

          <div className="flex flex-wrap -mx-3 mt-4">
            <div className="w-full md:w-1/2 px-3">
              <label
                className="block uppercase tracking-wide text-gray-500 text-xs font-bold mb-2"
                htmlFor="athletePhone"
              >
                Athlete Phone
              </label>
              <div className="relative">
                <FontAwesomeIcon
                  icon={faPhone}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                />

                <input
                  name="athletePhone"
                  className={`appearance-none block w-full text-gray-900 border border-gray-300 rounded p-3 pl-10 mb-3 leading-tight focus:outline-none ${!isEditing ? "focus:bg-gray-100" : "focus:bg-white"} placeholder-gray-900 ${!isEditing ? "bg-gray-100" : ""}`}
                  id="athletePhone"
                  type="tel"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={profileData.athletePhone ?? ""}
                  onChange={(e) => {
                    const numericValue = e.target.value.replace(/\D/g, "");
                    handleChange({
                      target: {
                        name: e.target.name,
                        value: numericValue,
                      },
                    });
                  }}
                  readOnly={!isEditing}
                />
              </div>
            </div>

            <div className="w-full md:w-1/2 px-3">
              <label
                className="block uppercase tracking-wide text-gray-500 text-xs font-bold mb-2"
                htmlFor="parentPhone"
              >
                Parent Phone
              </label>
              <div className="relative">
                <FontAwesomeIcon
                  icon={faPhone}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                />
                <input
                  name="parentPhone"
                  className={`appearance-none block w-full text-gray-900 border border-gray-300 rounded p-3 pl-10 mb-3 leading-tight focus:outline-none ${!isEditing ? "focus:bg-gray-100" : "focus:bg-white"} placeholder-gray-900 ${!isEditing ? "bg-gray-100" : ""}`}
                  id="parentPhone"
                  type="tel"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={profileData.parentPhone ?? ""}
                  onChange={(e) => {
                    const numericValue = e.target.value.replace(/\D/g, "");
                    handleChange({
                      target: {
                        name: e.target.name,
                        value: numericValue,
                      },
                    });
                  }}
                  readOnly={!isEditing}
                />
              </div>
            </div>
          </div>

          <div className="w-full mb-6">
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 flex flex-col md:flex-row md:items-start md:justify-between gap-6">
              {/* GPA */}
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xl text-gray-300 opacity-80 transition-opacity duration-200">
                    🎓
                  </span>
                  <label
                    htmlFor="gpa"
                    className="uppercase tracking-wide text-gray-500 text-xs font-bold"
                  >
                    GPA
                  </label>
                </div>
                <input
                  name="gpa"
                  id="gpa"
                  type="text"
                  value={profileData.gpa ?? ""}
                  onChange={handleChange}
                  readOnly={!isEditing}
                  className={`appearance-none block w-full text-gray-900 border border-gray-300 rounded p-3 leading-tight focus:outline-none ${!isEditing ? "focus:bg-gray-100" : "focus:bg-white"} placeholder-gray-900 ${!isEditing ? "bg-gray-100" : ""}`}
                  placeholder="Enter GPA"
                />
              </div>

              {/* Transcript */}
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xl">📄</span>
                  <span className="uppercase tracking-wide text-gray-500 text-xs font-bold">
                    Transcript
                  </span>
                </div>
                <TranscriptUploader
                  transcriptFilePath={profileData.transcriptFilePath}
                  transcriptFileId={profileData.transcriptFileId}
                  onUpdateFilePath={(url) =>
                    setProfileData((prev) => ({
                      ...prev,
                      transcriptFilePath: url,
                    }))
                  }
                  onUpdateFileId={(id) =>
                    setProfileData((prev) => ({
                      ...prev,
                      transcriptFileId: id,
                    }))
                  }
                  apiInstance={apiInstance}
                  hasSubscriptionAccess={hasSubscriptionAccess}
                  onBlockedUpload={() => setOpenPremiumFeatureModal(true)}
                />
              </div>
            </div>
          </div>

          {isEditing && (
            <div className="flex justify-center mt-2">
              <button
                type="submit"
                className="bg-blue-500 text-white px-4 py-2 rounded"
              >
                Save
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

const TranscriptUploader = ({
  transcriptFilePath,
  transcriptFileId,
  onUpdateFilePath,
  onUpdateFileId,
  apiInstance,
  hasSubscriptionAccess,
  onBlockedUpload,
}) => {
  const fileInputRef = useRef(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [fileIdToDelete, setFileIdToDelete] = useState(null);

  const handleFileSelect = () => {
    if (!hasSubscriptionAccess) {
      onBlockedUpload?.();
      return;
    }

    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const response = await insertAthleteFileService(
        apiInstance,
        "Transcript",
        file,
      );
      const { fileUrl, uploadedId } = response.data;

      onUpdateFilePath(fileUrl);
      onUpdateFileId(uploadedId);

      toast.success("File uploaded successfully!");
    } catch (error) {
      toast.error(
        "Error uploading file: " + (error.response?.data?.message || "Error"),
      );
    }
  };

  const handleFileDelete = () => {
    setFileIdToDelete(transcriptFileId);
    setShowConfirmModal(true);
  };

  const confirmDelete = async () => {
    try {
      await deleteAthleteFileService(
        apiInstance,
        "AthleteFile",
        fileIdToDelete,
      );
      toast.success("File deleted successfully!");
      onUpdateFilePath("");
      onUpdateFileId(null);
    } catch (error) {
      toast.error("Error deleting file: " + error.response?.data?.message);
    } finally {
      setShowConfirmModal(false);
      setFileIdToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowConfirmModal(false);
    setFileIdToDelete(null);
  };

  return (
    <div className="w-full px-3">
      <ConfirmModal
        show={showConfirmModal}
        tital="Delete Transcript?"
        message="Are you sure you want to delete this file?"
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />

      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={handleFileSelect}
          className="bg-blue-500 text-white px-4 py-2 rounded cursor-pointer flex items-center gap-2"
        >
          <FontAwesomeIcon icon={faArrowUpFromBracket} className="text-lg" />
          <span className="text-sm">Click to upload file</span>
        </button>

        <input
          type="file"
          accept=".pdf,.jpg,.jpeg"
          ref={fileInputRef}
          onChange={handleFileChange}
          style={{ display: "none" }}
        />

        {transcriptFilePath && (
          <div className="flex items-center gap-2 bg-gray-100 px-3 py-2 rounded-full">
            <a
              href={transcriptFilePath}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-500 text-sm font-medium truncate max-w-[200px] hover:underline"
            >
              {transcriptFilePath.split("/").pop()}
            </a>
            <button
              type="button"
              onClick={handleFileDelete}
              className="text-red-600 hover:text-red-800"
            >
              <FontAwesomeIcon icon={faTrash} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TranscriptUploader;
